import React from 'react';
import { Container, Typography, Box, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { motion } from 'framer-motion';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import LockIcon from '@mui/icons-material/Lock';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import FilterListIcon from '@mui/icons-material/FilterList';
import { GlassPaper, fadeInUp, scaleIn } from '../../styles/styledComponents';
import { styled } from '@mui/material/styles';

const MotionBox = motion(Box);

const PageContainer = styled(Container)(({ theme }) => ({
  fontFamily: '"Orbitron", "Noto Sans JP", sans-serif',
  paddingTop: theme.spacing(10),
  paddingBottom: theme.spacing(8),
  minHeight: '100vh',
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  
  '&::before': {
    content: '""',
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `
      radial-gradient(circle at 20% 50%, rgba(0, 255, 255, 0.1) 0%, transparent 50%),
      radial-gradient(circle at 80% 80%, rgba(255, 0, 255, 0.1) 0%, transparent 50%),
      radial-gradient(circle at 40% 20%, rgba(0, 255, 0, 0.1) 0%, transparent 50%)
    `,
    pointerEvents: 'none',
    zIndex: -1,
  },
}));

const PageTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 900,
  background: 'linear-gradient(45deg, #00ffff 0%, #ff00ff 50%, #00ff00 100%)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
  marginBottom: theme.spacing(2),
  textTransform: 'uppercase',
  letterSpacing: '0.1em',
  textAlign: 'center',
  filter: 'drop-shadow(0 0 30px rgba(0, 255, 255, 0.5))',
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  color: theme.palette.neon.cyan,
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  marginBottom: theme.spacing(2),
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  
  '& .MuiSvgIcon-root': {
    filter: `drop-shadow(0 0 10px ${theme.palette.neon.cyan})`,
  },
}));

const FeatureCard = styled(motion.div)(({ theme, variant }) => ({
  padding: theme.spacing(3),
  borderRadius: 0,
  border: '2px solid',
  borderColor: variant === 'premium' ? theme.palette.neon.lime : theme.palette.neon.orange,
  backgroundColor: 'rgba(37, 37, 48, 0.8)',
  backdropFilter: 'blur(16px)',
  position: 'relative',
  overflow: 'hidden',
  transition: 'all 0.3s ease',
  
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: variant === 'premium' 
      ? `radial-gradient(circle at center, rgba(0, 255, 0, 0.1) 0%, transparent 70%)`
      : `radial-gradient(circle at center, rgba(255, 170, 0, 0.1) 0%, transparent 70%)`,
    opacity: 0,
    transition: 'opacity 0.3s ease',
  },
  
  '&:hover': {
    transform: 'translateY(-5px) scale(1.02)',
    boxShadow: variant === 'premium'
      ? `0 0 30px ${theme.palette.neon.lime}`
      : `0 0 30px ${theme.palette.neon.orange}`,
    borderColor: variant === 'premium' 
      ? theme.palette.neon.lime 
      : theme.palette.neon.orange,
    
    '&::before': {
      opacity: 1,
    },
  },
}));

const FeatureList = styled(Box)(({ theme }) => ({
  listStyle: 'none',
  padding: 0,
  margin: 0,
  
  '& li': {
    position: 'relative',
    paddingLeft: theme.spacing(3),
    marginBottom: theme.spacing(1.5),
    color: theme.palette.text.secondary,
    fontSize: '0.95rem',
    lineHeight: 1.6,
    
    '&::before': {
      content: '"▸"',
      position: 'absolute',
      left: 0,
      color: 'inherit',
      fontWeight: 700,
      fontSize: '1.2rem',
    },
  },
}));

const BackButton = styled(Button)(({ theme }) => ({
  background: 'linear-gradient(45deg, #00ffff 0%, #00aaff 100%)',
  color: '#000',
  padding: theme.spacing(1.5, 4),
  borderRadius: 0,
  border: '2px solid #00ffff',
  fontWeight: 700,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  boxShadow: '0 0 20px rgba(0, 255, 255, 0.5)',
  transition: 'all 0.3s ease',
  
  '&:hover': {
    background: 'linear-gradient(45deg, #00ffff 0%, #00aaff 100%)',
    transform: 'translateY(-2px)',
    boxShadow: '0 0 30px rgba(0, 255, 255, 0.8)',
    borderColor: '#66ffff',
  },
}));

const AboutPage = () => {
  const navigate = useNavigate();

  return (
    <PageContainer maxWidth="md">
      <MotionBox
        initial="initial"
        animate="animate"
        exit="exit"
        variants={fadeInUp}
      >
        <Box sx={{ mb: 6 }}>
          <PageTitle variant="h3" component="h1">
            About ClashSync
          </PageTitle>
          <Typography 
            variant="subtitle1" 
            sx={{ 
              color: 'text.secondary',
              maxWidth: '600px',
              margin: '0 auto',
              textAlign: 'center',
              fontWeight: 500,
              letterSpacing: '0.05em',
            }}
          >
            Eスポーツの大会情報を、より便利に、より使いやすく
          </Typography>
        </Box>

        <GlassPaper
          component={motion.div}
          variants={scaleIn}
          initial="initial"
          animate="animate"
          sx={{ p: 5, mb: 4 }}
        >
          <Box sx={{ mb: 5 }}>
            <SectionTitle variant="h5">
              <CalendarMonthIcon />
              Service Overview
            </SectionTitle>
            <Typography variant="body1" sx={{ 
              color: 'text.secondary', 
              lineHeight: 1.8,
              fontFamily: '"Rajdhani", "Noto Sans JP", sans-serif',
            }}>
              ClashSyncは、Eスポーツの大会情報をカレンダー形式で確認できるプラットフォームです。
              大会スケジュールを一目で把握し、興味のある大会を簡単に見つけることができます。
            </Typography>
          </Box>

          <Box sx={{ mb: 5 }}>
            <SectionTitle variant="h5">
              Available Features
            </SectionTitle>
            
            <Box sx={{ 
              display: 'grid', 
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              gap: 3,
              mt: 3
            }}>
              {/* 未ログインユーザー */}
              <FeatureCard
                variants={scaleIn}
                whileHover={{ scale: 1.02 }}
                variant="basic"
              >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
                  <LockOpenIcon sx={{ 
                    color: 'neon.orange',
                    filter: 'drop-shadow(0 0 10px currentColor)',
                  }} />
                  <Typography variant="h6" sx={{ 
                    fontWeight: 700,
                    color: 'neon.orange',
                    letterSpacing: '0.05em',
                  }}>
                    Guest Mode
                  </Typography>
                </Box>
                <FeatureList component="ul">
                  <li style={{ color: '#ffaa00' }}>当月のみの大会情報閲覧</li>
                  <li style={{ color: '#ffaa00' }}>基本的なカレンダービュー</li>
                  <li style={{ color: '#ffaa00' }}>フィルタリング機能なし</li>
                </FeatureList>
              </FeatureCard>

              {/* ログインユーザー */}
              <FeatureCard
                variants={scaleIn}
                whileHover={{ scale: 1.02 }}
                variant="premium"
              >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
                  <LockIcon sx={{ 
                    color: 'neon.lime',
                    filter: 'drop-shadow(0 0 10px currentColor)',
                  }} />
                  <Typography variant="h6" sx={{ 
                    fontWeight: 700,
                    color: 'neon.lime',
                    letterSpacing: '0.05em',
                  }}>
                    Member Mode
                  </Typography>
                </Box>
                <FeatureList component="ul">
                  <li style={{ color: '#00ff00' }}>過去1年分の大会履歴</li>
                  <li style={{ color: '#00ff00' }}>先3ヶ月の大会情報</li>
                  <li style={{ color: '#00ff00' }}>大会別フィルタリング</li>
                  <li style={{ color: '#00ff00' }}>チーム別フィルタリング</li>
                </FeatureList>
              </FeatureCard>
            </Box>
          </Box>

          <Box sx={{ mb: 5 }}>
            <SectionTitle variant="h5">
              <FilterListIcon />
              Filter System
            </SectionTitle>
            <Typography variant="body1" sx={{ 
              color: 'text.secondary', 
              lineHeight: 1.8, 
              mb: 2,
              fontFamily: '"Rajdhani", "Noto Sans JP", sans-serif',
            }}>
              ログインユーザーは以下の便利なフィルタリング機能を利用できます：
            </Typography>
            <FeatureList component="ul">
              <li>大会タイプによるフィルタリング（メジャー、マイナー、地域大会など）</li>
              <li>参加チームによるフィルタリング</li>
              <li>開催形式によるフィルタリング（オンライン、オフライン）</li>
            </FeatureList>
          </Box>
        </GlassPaper>

        <Box display="flex" justifyContent="center">
          <BackButton
            component={motion.button}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/')}
          >
            Back to Home
          </BackButton>
        </Box>
      </MotionBox>
    </PageContainer>
  );
};

export default AboutPage;
